import { isPlatformBrowser } from '@angular/common';
import { HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject, PLATFORM_ID } from '@angular/core';
import { isBackendUrl } from '@gs/shared';
import { EnvironmentService, ScriptService } from '@gs/shared/services';
import { tap } from 'rxjs';

/**
 * Reads the `JavaScript` field a response carries and hands it to the {@link ScriptService}.
 *
 * CS3 delivers the scripts produced while an action runs in that field
 * (`PageMetaData.PopJavaScripts()`); the tracking events bound to an action — add_to_cart,
 * purchase, login, sign_up, … — travel this way. CS4 reuses that contract instead of building
 * events in the frontend, so this interceptor is the single place where those scripts run.
 *
 * The backend writes the field in two spots: at the top level of a `DictJsonResult`, and inside
 * the nested `Result` of an action that ends in a redirect (login and registration). Both are
 * PascalCase, because the interceptor sees the raw payload before `DataProvider.mapCasesReverse`
 * converts it.
 *
 * Browser only: during SSR there is no document to inject into, and tracking belongs to the user's
 * session rather than to the rendered response. Responses from any other origin are ignored: the
 * field is executed as code, so only the backend that defines the contract may fill it.
 */
export const trackingInterceptor: HttpInterceptorFn = (req, next) => {
  if (!isPlatformBrowser(inject(PLATFORM_ID))) return next(req);

  if (!isBackendUrl(req.url, inject(EnvironmentService).env.BACKEND_URL)) return next(req);

  const scriptService = inject(ScriptService);

  return next(req).pipe(
    tap((event) => {
      if (!(event instanceof HttpResponse)) return;

      const body = event.body as Record<string, unknown> | null;
      if (!body || typeof body !== 'object') return;

      const nested = body['Result'] as Record<string, unknown> | undefined;
      for (const code of [body['JavaScript'], nested?.['JavaScript']]) {
        if (typeof code === 'string') scriptService.injectResponseScripts(code);
      }
    })
  );
};

